import { v4 as uuidv4 } from 'uuid';
import {
  sendProjectileSpawn,
  sendProjectileDespawn,
} from '../net/combatEvents';
import { dungeonService } from './DungeonService';
import { mapService } from './MapService';
import { netService } from './NetService';
import { EFFECT_HANDLERS } from '../effect-system/effectHandlers';
import { resolveShapeCells } from '../utils/shape.utils';
import { getTargetPosition } from '../utils/map.utils';
import { DungeonEntity } from '../entities/DungeonEntity';
import type { DirectionEnum } from '../types/enums';
import type {
  SpellDefinition,
  SpellEffect,
  EffectContext,
} from '../types/spell.types';
import type { ActiveProjectile } from '../types/projectile.types';

/** Render-only view shared by host ActiveProjectile and client projectiles. */
export interface ProjectileView {
  id: string;
  sprite: string;
  direction: DirectionEnum;
  dx: number;
  dy: number;
  cellX: number;
  cellY: number;
  progress: number;
}

interface ClientProjectile extends ProjectileView {
  cellsPerTick: number;
  rangeMax: number;
  cellsTraveled: number;
}

class ProjectileService {
  private static instance: ProjectileService;
  private projectiles: Map<string, ActiveProjectile> = new Map();
  private clientProjectiles: Map<string, ClientProjectile> = new Map();

  private constructor() {}

  public static getInstance(): ProjectileService {
    if (!ProjectileService.instance) {
      ProjectileService.instance = new ProjectileService();
    }
    return ProjectileService.instance;
  }

  public spawn(args: {
    owner: DungeonEntity;
    spell: SpellDefinition;
    direction: DirectionEnum;
    impactEffects: SpellEffect[];
  }): void {
    const cfg = args.spell.projectile;
    if (!cfg) {
      return;
    }
    const step = getTargetPosition(
      args.owner.positionX,
      args.owner.positionY,
      args.direction,
    );
    const id = uuidv4();
    this.projectiles.set(id, {
      id,
      owner: args.owner,
      spell: args.spell,
      impactEffects: args.impactEffects,
      sprite: cfg.sprite,
      direction: args.direction,
      dx: step.x - args.owner.positionX,
      dy: step.y - args.owner.positionY,
      cellX: args.owner.positionX,
      cellY: args.owner.positionY,
      progress: 0,
      cellsPerTick: cfg.speed / 60,
      cellsTraveled: 0,
      rangeMax: cfg.range ?? 1,
      pierce: cfg.pierce ?? false,
      triggerOnMiss: cfg.triggerOnMiss ?? false,
      friendlyFire: args.spell.friendlyFire ?? false,
      hitIds: new Set(),
    });
    sendProjectileSpawn({
      id,
      sprite: cfg.sprite,
      originX: args.owner.positionX,
      originY: args.owner.positionY,
      direction: args.direction,
      speed: cfg.speed,
      range: cfg.range ?? 1,
    });
  }

  /** Client: spawn a replicated projectile to dead-reckon locally. */
  public addClientProjectile(e: {
    id: string;
    sprite: string;
    originX: number;
    originY: number;
    direction: DirectionEnum;
    speed: number;
    range: number;
  }): void {
    const step = getTargetPosition(0, 0, e.direction);
    this.clientProjectiles.set(e.id, {
      id: e.id,
      sprite: e.sprite,
      direction: e.direction,
      dx: step.x,
      dy: step.y,
      cellX: e.originX,
      cellY: e.originY,
      progress: 0,
      cellsPerTick: e.speed / 60,
      rangeMax: e.range,
      cellsTraveled: 0,
    });
  }

  public removeClientProjectile(id: string): void {
    this.clientProjectiles.delete(id);
  }

  /** Client: advance replicated projectiles in a straight line; self-expire at
   *  range as a safety net if the despawn event is lost. */
  public tickClientProjectiles(deltaTime: number): void {
    const dead: string[] = [];
    this.clientProjectiles.forEach((p) => {
      const step = p.cellsPerTick * deltaTime;
      p.cellX += p.dx * step;
      p.cellY += p.dy * step;
      p.cellsTraveled += step;
      if (p.cellsTraveled > p.rangeMax + 1) {
        dead.push(p.id);
      }
    });
    dead.forEach((id) => this.clientProjectiles.delete(id));
  }

  /** Renderer source: host/offline render the simulated projectiles, the client
   *  renders the replicated ones. */
  public getRenderProjectiles(): ProjectileView[] {
    if (netService.isClient()) {
      return Array.from(this.clientProjectiles.values());
    }
    return Array.from(this.projectiles.values());
  }

  public reset(): void {
    this.projectiles.clear();
    this.clientProjectiles.clear();
  }

  public update(deltaTime: number): void {
    const dead: string[] = [];
    this.projectiles.forEach((p) => {
      // Check the cell the projectile currently occupies every tick — not only
      // on cell entry — so a target that walks INTO the projectile's cell
      // between advances is still hit (a moving entity would otherwise slip
      // through during the ticks the projectile sits in a cell).
      if (this.tryImpact(p, dead)) {
        return;
      }
      p.progress += p.cellsPerTick * deltaTime;
      while (p.progress >= 1) {
        p.progress -= 1;
        const next = getTargetPosition(p.cellX, p.cellY, p.direction);
        p.cellX = next.x;
        p.cellY = next.y;
        p.cellsTraveled += 1;

        if (p.cellsTraveled > p.rangeMax) {
          // cellX/cellY is the overflow cell; last valid cell is one step back
          this.impactOnMiss(p, p.cellX - p.dx, p.cellY - p.dy);
          dead.push(p.id);
          return;
        }
        if (!mapService.isWalkable(p.cellX, p.cellY, true)) {
          // cellX/cellY is the wall cell; last valid cell is one step back
          this.impactOnMiss(p, p.cellX - p.dx, p.cellY - p.dy);
          dead.push(p.id);
          return;
        }
        if (this.tryImpact(p, dead)) {
          return;
        }
      }
    });
    dead.forEach((id) => {
      this.projectiles.delete(id);
      sendProjectileDespawn(id);
    });
  }

  /**
   * Resolves a collision at the projectile's current cell. Returns true when the
   * projectile is destroyed (non-pierce hit) and must be skipped for this tick.
   */
  private tryImpact(p: ActiveProjectile, dead: string[]): boolean {
    const entity = dungeonService.getEntityAt(p.cellX, p.cellY, p.owner.id);
    if (!entity || !this.isValidTarget(p, entity)) {
      return false;
    }
    this.resolveImpact(p, p.cellX, p.cellY);
    if (p.pierce) {
      p.hitIds.add(entity.id);
      return false;
    }
    dead.push(p.id);
    return true;
  }

  private isValidTarget(p: ActiveProjectile, entity: DungeonEntity): boolean {
    if (p.hitIds.has(entity.id)) {
      return false;
    }
    if (p.friendlyFire) {
      return true;
    }
    return entity.type !== p.owner.type;
  }

  private runImpactAt(p: ActiveProjectile, x: number, y: number): void {
    // Impact shapes resolve around the impact cell — both the target-centered
    // (SINGLE/CIRCLE/CROSS/CONE) and the origin-centered (LINE/SWIPE) shapes use
    // the impact point, NOT the owner's live position (it may have moved in flight).
    const affectedCells = resolveShapeCells(p.spell.shapes, {
      targetX: x,
      targetY: y,
      casterX: x,
      casterY: y,
      direction: p.direction,
      defaultRange: p.spell.range,
    });
    const ctx: EffectContext = {
      caster: p.owner,
      spell: p.spell,
      direction: p.direction,
      targetX: x,
      targetY: y,
      affectedCells,
    };
    for (const eff of p.impactEffects) {
      EFFECT_HANDLERS[eff.handler]?.(ctx, { ...eff.params });
    }
    dungeonService.markNeedsRender();
  }

  private resolveImpact(
    p: ActiveProjectile,
    impactX: number,
    impactY: number,
  ): void {
    this.runImpactAt(p, impactX, impactY);
  }

  private impactOnMiss(p: ActiveProjectile, x: number, y: number): void {
    if (p.triggerOnMiss) {
      this.runImpactAt(p, x, y);
    }
  }
}

export const projectileService = ProjectileService.getInstance();
